type BusinessJson = {
  business: {
    legalName: string;
    displayName: string;
    location: {
      addressLine1: string;
      cityRegionPostal: string;
      countryCode: string;
    };
    contact: {
      phone: string;
      instagram?: string;
    };
    hours?: {
      // optional: you can later parse structured hours
      display?: string;
    };
  };
  seo: {
    title: string;
    description: string;
    domain: string; // "marrielbarber.com"
    ogImage: string; // "/clients/marriel/assets/og.png"
  };
};

function upsertMetaByName(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLinkRel(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id: string, json: unknown) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(json);
}

function absoluteUrl(domain: string, pathOrUrl: string) {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const trimmed = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `https://${domain}${trimmed}`;
}

export function applySeoFromBusiness(data: BusinessJson) {
  const { business, seo } = data;

  const siteUrl = `https://${seo.domain}/`;
  const ogImageAbs = absoluteUrl(seo.domain, seo.ogImage);

  // Title + description
  document.title = seo.title;
  upsertMetaByName("description", seo.description);

  // Canonical
  upsertLinkRel("canonical", siteUrl);

  // OG
  upsertMetaByProperty(
    "og:site_name",
    business.displayName || business.legalName,
  );
  upsertMetaByProperty("og:title", seo.title);
  upsertMetaByProperty("og:description", seo.description);
  upsertMetaByProperty("og:type", "website");
  upsertMetaByProperty("og:url", siteUrl);
  upsertMetaByProperty("og:image", ogImageAbs);
  upsertMetaByProperty("og:image:width", "1200");
  upsertMetaByProperty("og:image:height", "630");

  // Twitter
  upsertMetaByName("twitter:card", "summary_large_image");
  upsertMetaByName("twitter:title", seo.title);
  upsertMetaByName("twitter:description", seo.description);
  upsertMetaByName("twitter:image", ogImageAbs);

  // JSON-LD: BarberShop (minimal, you can enhance later with geo + structured hours)
  const instagram = business.contact.instagram
    ? `https://instagram.com/${business.contact.instagram.replace(/^@/, "")}`
    : undefined;

  const [city, rest] = business.location.cityRegionPostal
    .split(",")
    .map((s) => s.trim());
  const region = rest?.split(" ")[0] ?? "CT";
  const postal = rest?.split(" ").slice(1).join(" ") ?? "";

  upsertJsonLd("fx-jsonld-business", {
    "@context": "https://schema.org",
    "@type": "BarberShop",
    name: business.legalName,
    alternateName: business.displayName,
    url: siteUrl,
    image: ogImageAbs,
    telephone: business.contact.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: business.location.addressLine1,
      addressLocality: city || "Danbury",
      addressRegion: region,
      postalCode: postal,
      addressCountry: business.location.countryCode,
    },
    ...(instagram ? { sameAs: [instagram] } : {}),
  });

  // JSON-LD: Creator (Framixor + LPG) — not the site identity, just publisher
  upsertJsonLd("fx-jsonld-creator", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Framixor",
    url: "https://framixor.com",
    parentOrganization: {
      "@type": "Organization",
      name: "Loiola Property Group",
      url: "https://loiolapropertygroup.com",
    },
  });
}
